import ErrorHandler from '@/utils/ErrorHandler';
import { supabase, queryClient } from './client';
import { getCurrentOrgID, getOrganization, upsertImportAssociations } from './organization';
import { reportRangeBounds } from '../utils/reportDates';;
import findAssociativeChanges from '@/utils/findAssociativeChanges';
import * as Crypto from 'expo-crypto';
import { DateTime } from 'luxon';
import { insertActivity } from './activity';
import { buildUpdateDetails, joinDetails } from '../utils/buildActivityDetails';
import { importFields } from '@/utils/constants/importFields';
import { isReportableContribution } from '@/utils/contributionStatus';

// Columns on ContributionTransactions that are timestamptz and may receive
// date-only values from a CSV import. Date-only values are interpreted as
// midnight in the org's timezone so they don't shift to the previous day when
// the client renders them in a negative-UTC-offset timezone.
const TRANSACTION_DATETIME_COLUMNS = ['transactionDate'];

/**
 * If a CSV value is a date-only string (no time component), interpret it as
 * midnight in the org's timezone and return the UTC ISO string. This prevents
 * the date from shifting when displayed in a timezone behind UTC.
 * Values that already include a time component or timezone info are returned as-is.
 */
const adjustDateOnlyForTimezone = (value, orgTimezone) => {
    if (!value || typeof value !== 'string') return value;
    const trimmed = value.trim();
    // Skip values with a time component (T separator, colon, or " HH:MM" suffix)
    if (/[T:]/.test(trimmed) || /\s\d{1,2}:\d{2}/.test(trimmed)) return value;

    // Try common date formats, interpreting each as midnight in the org timezone
    let dt = DateTime.fromISO(trimmed, { zone: orgTimezone });
    if (!dt.isValid) dt = DateTime.fromFormat(trimmed, 'MM/dd/yyyy', { zone: orgTimezone });
    if (!dt.isValid) dt = DateTime.fromFormat(trimmed, 'M/d/yyyy', { zone: orgTimezone });
    if (!dt.isValid) dt = DateTime.fromFormat(trimmed, 'yyyy-MM-dd', { zone: orgTimezone });
    if (!dt.isValid) dt = DateTime.fromFormat(trimmed, 'MM-dd-yyyy', { zone: orgTimezone });

    return dt.isValid ? dt.toUTC().toISO() : value;
};

// Contribution Batches
export const getBatches = async () => {
  const { data, error } = await supabase.rpc('get_batches_summary');

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getBatches", input: {error}})
    throw Error(error?.message);
  }

  return data || [];
};

export const getBatch = async (id) => {
  const orgID = await getCurrentOrgID();
  let query = supabase.from('ContributionBatches')
    .select('id, name, isOpen, batchNumber, openedAt, isReachGiving').eq('organizationID', orgID);

  if(!id){
    query = query.limit(1).order('batchNumber', { ascending: false});
  }else{
    query = query.eq('id', id);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getBatch", input: {id, error}})
    throw Error(error?.message);
  }

  return data || {};
};

const BATCH_TRANSACTION_SELECT = (innerPerson = false) => `
  id, amount, transactionDate, createdAt, note, methodDetailValue, personID, methodID, batchID,
  Person:People${innerPerson ? '!inner' : ''}(id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey),
  Method:ContributionMethods(id, name, detailName),
  Funds:ContributionFundTransactions(amount, Fund:ContributionFunds(id, name))
`;

const BATCH_TRANSACTION_PAGE_SIZE = 25;

export const getBatchTransactions = async ({ batchID, pageParam = 0, searchText = '', sortConfig = null }) => {
  const orgID = await getCurrentOrgID();
  const offset = (pageParam || 0) * BATCH_TRANSACTION_PAGE_SIZE;

  const search = searchText?.trim().replace(/[(),]/g, ' ').trim();

  let query = supabase
    .from('ContributionTransactions')
    .select(BATCH_TRANSACTION_SELECT(!!search))
    .in('status', ['PROCESSING', 'SUCCEEDED'])
    .eq('organizationID', orgID)
    .eq('batchID', batchID);

  if (search) {
    query = query.or(`firstName.ilike.%${search}%,lastName.ilike.%${search}%`, { referencedTable: 'Person' });
  }

  if (sortConfig) {
    const { key, direction } = sortConfig;
    const ascending = direction === 'asc';
    switch (key) {
      case 'amount':
        query = query.order('amount', { ascending }).order('id', { ascending });
        break;
      case 'date':
        query = query.order('transactionDate', { ascending }).order('id', { ascending });
        break;
      case 'note':
        query = query.order('note', { ascending: true, nullsFirst: false }).order('id', { ascending: true });
        break;
      case 'details':
        query = query.order('methodDetailValue', { ascending: true, nullsFirst: false }).order('id', { ascending: true });
        break;
      case 'person':
        query = query.order('Person(firstName)', { ascending, nullsFirst: false })
          .order('Person(lastName)', { ascending, nullsFirst: false })
          .order('id', { ascending });
        break;
      case 'method':
        query = query.order('methodID', { ascending }).order('id', { ascending });
        break;
      default:
        query = query.order('createdAt', { ascending: false }).order('id', { ascending: false });
    }
  } else {
    query = query.order('createdAt', { ascending: false }).order('id', { ascending: false });
  }

  query = query.range(offset, offset + BATCH_TRANSACTION_PAGE_SIZE - 1);

  const { data, error } = await query;

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getBatchTransactions", input: { batchID, error } });
    throw Error(error?.message);
  }

  return {
    data: data || [],
    nextPage: (data?.length || 0) < BATCH_TRANSACTION_PAGE_SIZE ? undefined : pageParam + 1,
  };
};

export const getBatchStats = async (batchID) => {
  const { data, error } = await supabase.rpc('get_batch_stats', { p_batch_id: batchID });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getBatchStats", input: { batchID, error } });
    throw Error(error?.message);
  }

  return data || { totalAmount: 0, transactionCount: 0, uniqueGivers: 0, byMethod: {}, byFund: {} };
};

export const getAllBatchTransactions = async (batchID) => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase
    .from('ContributionTransactions')
    .select(BATCH_TRANSACTION_SELECT())
    .in('status', ['PROCESSING', 'SUCCEEDED'])
    .eq('organizationID', orgID)
    .eq('batchID', batchID)
    .order('createdAt', { ascending: false });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getAllBatchTransactions", input: { batchID, error } });
    throw Error(error?.message);
  }

  return data || [];
};


export const saveBatch = async (batchData) => {
  const originalValues = batchData.originalValues || null;
  delete batchData.originalValues;
  if(batchData.id && batchData.id !== 'undefined'){
    return updateBatch(batchData, originalValues);
  }
  else{
    return createBatch(batchData);
  }
};

export const deleteBatch = async (batchID) => {
  const orgID = await getCurrentOrgID();

  const { count, error: countError } = await supabase
    .from('ContributionTransactions')
    .select('id', { count: 'exact', head: true })
    .eq('organizationID', orgID)
    .eq('batchID', batchID);

  if (countError) {
    ErrorHandler(Error(countError?.message), { query: "deleteBatch", input: { batchID, error: countError } });
    throw Error(countError?.message);
  }

  if (count > 0) {
    throw Error("Cannot delete a batch that has transactions.");
  }

  const { error: deleteError } = await supabase
    .from('ContributionBatches')
    .delete()
    .eq('id', batchID);

  if (deleteError) {
    ErrorHandler(Error(deleteError?.message), { query: "deleteBatch", input: { batchID, error: deleteError } });
    throw Error(deleteError?.message);
  }

  insertActivity({ subjectType: 'Batch', subjectID: batchID, verb: 'delete' }).catch(() => {});

  return { id: batchID };
};

const updateBatch = async (batchData, originalValues = null) => {
  const { data, error } = await supabase.from('ContributionBatches').update({name: batchData.name, isOpen: batchData.isOpen}).eq('id', batchData.id).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "updateBatch", input: {batchData, error}})
    throw Error(error?.message);
  }

  const details = originalValues ? buildUpdateDetails(originalValues, batchData, { name: { label: 'Name' }, isOpen: { label: 'Open' } }) : null;
  insertActivity({ subjectType: 'Batch', subjectID: data.id, subjectName: data.name, verb: 'update', details }).catch(() => {});

  return data;
};

const createBatch = async (batchData) => {
  const orgID = await getCurrentOrgID();
  const lastBatchNumber = await supabase.from('ContributionBatches').select("batchNumber").eq("organizationID", orgID).order("batchNumber", { ascending: false }).limit(1).maybeSingle();

  if(lastBatchNumber.error){
    ErrorHandler(Error(lastBatchNumber.error?.message), {query: "createBatch", input: {batchData, error: lastBatchNumber.error}})
    throw Error(lastBatchNumber.error?.message);
  }

  if(lastBatchNumber.data?.batchNumber){
    batchData.batchNumber = lastBatchNumber.data.batchNumber + 1;
  }else{
    batchData.batchNumber = 1;
  }

  batchData.organizationID = orgID;

  const { data, error } = await supabase.from('ContributionBatches').insert(batchData).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "createBatch", input: {batchData, error}})
    throw Error(error?.message);
  }

  insertActivity({ subjectType: 'Batch', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

  return data;
};

// Contribution Funds
export const getFunds = async () => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionFunds').select('*').eq('organizationID', orgID);

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getFunds", input: {error}})
    throw Error(error?.message);
  }

  return data || [];
};

export const getFund = async (id) => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionFunds').select('*, Transactions:ContributionFundTransactions(amount, Transaction:ContributionTransactions(status, Batch:ContributionBatches(*)))').eq('organizationID', orgID).eq('id', id).maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getFund", input: {id, error}})
    throw Error(error?.message);
  }

  if (data?.Transactions) data.Transactions = data.Transactions.filter(item => item.Transaction && isReportableContribution(item.Transaction));
  return data || {};
};

export const saveFund = async (fundData) => {
  const originalValues = fundData.originalValues || null;
  delete fundData.originalValues;
  if(fundData.id){
    return updateFund(fundData.id, fundData, originalValues);
  }
  else{
    return createFund(fundData);
  }
};

const createFund = async (fundData) => {
  fundData.organizationID = await getCurrentOrgID();
  fundData.isTaxDeductible = fundData.isTaxDeductible ?? true;
  const { data, error } = await supabase.from('ContributionFunds').insert(fundData).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "createFund", input: {fundData, error}})
    throw Error(error?.message);
  }

  insertActivity({ subjectType: 'Fund', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

  return data;
};

const updateFund = async (id, updates, originalValues = null) => {
  const { data, error } = await supabase.from('ContributionFunds').update(updates).eq('id', id).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "updateFund", input: {id, updates, error}})
    throw Error(error?.message);
  }

  const details = originalValues ? buildUpdateDetails(originalValues, updates, { name: { label: 'Name' }, isTaxDeductible: { label: 'Tax Deductible' } }) : null;
  insertActivity({ subjectType: 'Fund', subjectID: id, subjectName: data.name, verb: 'update', details }).catch(() => {});

  return data;
};

// Contribution Methods
export const getMethods = async () => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionMethods').select('*').eq('organizationID', orgID);

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getMethods", input: {error}})
    throw Error(error?.message);
  }

  return data || [];
};

export const getMethod = async (id) => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionMethods').select('*').eq('organizationID', orgID).eq('id', id).maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getMethod", input: {id, error}})
    throw Error(error?.message);
  }

  return data || {};
};

export const saveMethod = async (methodData) => {
  const originalValues = methodData.originalValues || null;
  delete methodData.originalValues;
  if(methodData.id){
    return updateMethod(methodData.id, methodData, originalValues);
  }
  else{
    return createMethod(methodData);
  }
};

const createMethod = async (methodData) => {
  methodData.organizationID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionMethods').insert(methodData).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "createMethod", input: {methodData, error}})
    throw Error(error?.message);
  }

  insertActivity({ subjectType: 'Method', subjectID: data.id, subjectName: data.name, verb: 'create' }).catch(() => {});

  return data;
};

const updateMethod = async (id, updates, originalValues = null) => {
  const { data, error } = await supabase.from('ContributionMethods').update(updates).eq('id', id).select().maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "updateMethod", input: {id, updates, error}})
    throw Error(error?.message);
  }

  const details = originalValues ? buildUpdateDetails(originalValues, updates, { name: { label: 'Name' } }) : null;
  insertActivity({ subjectType: 'Method', subjectID: id, subjectName: data.name, verb: 'update', details }).catch(() => {});

  return data;
};

export const getTransaction = async (id) => {
  const orgID = await getCurrentOrgID();
  const { data, error } = await supabase.from('ContributionTransactions').select('*, Method:ContributionMethods(id, name), Person:People(id, email, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Funds:ContributionFundTransactions(*, Fund:ContributionFunds(*))').eq('organizationID', orgID).eq('id', id).maybeSingle();

  if (error) {
    ErrorHandler(Error(error?.message), {query: "getTransaction", input: {id, error}})
    throw Error(error?.message);
  }

  return data || {};
};

export const saveTransaction = async (transactionData) => {
    const originalValues = transactionData.originalValues || null;
    delete transactionData.originalValues;
    const transaction = {
        ...transactionData,
        isAnonymous: undefined,
        Method: undefined,
        Funds: undefined,
        Person: undefined,
        personID: transactionData.personID || null,
        organizationID: await getCurrentOrgID()
    }

    if(transactionData.id){
        return updateTransaction(transactionData.id, transaction, transactionData.Funds, originalValues);
    }
    else{
        return createTransaction(transaction, transactionData.Funds);
    }
};

const updateTransaction = async (id, updates, funds, originalValues = null) => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase.from('ContributionTransactions').update(updates).eq('id', id).select('id, amount, transactionDate, Funds:ContributionFundTransactions(*, Fund:ContributionFunds(id, name)), Person:People(firstName, lastName), Method:ContributionMethods(name)').maybeSingle();

    if (error) {
        ErrorHandler(Error(error?.message), {query: "updateTransaction", input: {id, updates, error}})
        throw Error(error?.message);
    }

    if(funds){
       const { itemsToAdd: fundsToAdd, itemsToDelete: fundsToDelete, unchangedItems: fundsToUpdate } = findAssociativeChanges(funds, data.Funds.map(fund => fund.Fund));

       if(fundsToAdd.length > 0){
           const { error: error1 } = await supabase.from('ContributionFundTransactions').insert(fundsToAdd.map(fund => ({transactionID: id, fundID: fund.id, organizationID: orgID, amount: fund.amount})));

           if(error1){
               ErrorHandler(Error(error1?.message), {query: "updateTransaction", input: {id, fundsToAdd, error1}})
               throw Error(error1?.message);
           }
       }

       if(fundsToDelete.length > 0){
           const { error: error2 } = await supabase.from('ContributionFundTransactions').delete().eq('transactionID', id).in('fundID', fundsToDelete.map(fund => fund.id));

           if(error2){
               ErrorHandler(Error(error2?.message), {query: "updateTransaction", input: {id, fundsToDelete, error2}})
               throw Error(error2?.message);
           }
       }

       if(fundsToUpdate.length > 0){
           for(const fund of fundsToUpdate){
               const { error: error3 } = await supabase.from('ContributionFundTransactions').update({amount: fund.amount}).eq('transactionID', id).eq('fundID', fund.id).eq('organizationID', orgID);

               if(error3){
                   ErrorHandler(Error(error3?.message), {query: "updateTransaction", input: {id, fundsToUpdate, error3}})
                   throw Error(error3?.message);
               }
           }
       }
    }

    const personName = data.Person ? `${data.Person.firstName ?? ''} ${data.Person.lastName ?? ''}`.trim() : 'Contribution';
    const fmtMoney = (v) => typeof v === 'number' ? `$${v.toFixed(2)}` : String(v);
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : null;
    const scalarDetails = originalValues ? buildUpdateDetails(originalValues, updates, {
        amount: { label: 'Amount', format: fmtMoney },
        transactionDate: { label: 'Date', format: fmtDate },
    }) : null;
    const fundDetails = [];
    if (funds) {
        const { itemsToAdd: fundsToAdd, itemsToDelete: fundsToDelete, unchangedItems: fundsToUpdate } = findAssociativeChanges(funds, data.Funds.map(fund => fund.Fund));
        if(fundsToAdd?.length) fundDetails.push(`- Added fund(s): ${fundsToAdd.map(f => f.name).join(', ')}`);
        if(fundsToDelete?.length) fundDetails.push(`- Removed fund(s): ${fundsToDelete.map(f => f.name).join(', ')}`);
        if(fundsToUpdate?.length) fundDetails.push(`- Updated fund(s): ${fundsToUpdate.map(f => f.name).join(', ')}`);
    }
    insertActivity({ subjectType: 'Contribution', subjectID: id, subjectName: personName, verb: 'update', details: joinDetails(scalarDetails, fundDetails) }).catch(() => {});

    return data;
};

const createTransaction = async (transactionData, funds) => {
     const { data, error } = await supabase.from('ContributionTransactions').insert(transactionData).select('*, Person:People(firstName, lastName)').maybeSingle();

    if (error) {
        ErrorHandler(Error(error?.message), {query: "createTransaction", input: {transactionData, error}})
        throw Error(error?.message);
    }

    if(funds){
        const { data: fundData, error: fundError } = await supabase.from('ContributionFundTransactions').insert(funds.map(fund => ({transactionID: data.id, fundID: fund.id, organizationID: transactionData.organizationID, amount: fund.amount}))).select();

        if (fundError) {
        ErrorHandler(Error(fundError?.message), {query: "createTransaction", input: {funds, error: fundError}})
        throw Error(fundError?.message);
        }

        data.Funds = fundData;
    }

    const personName = data.Person ? `${data.Person.firstName ?? ''} ${data.Person.lastName ?? ''}`.trim() : 'Contribution';
    const amountStr = typeof data.amount === 'number' ? `$${data.amount.toFixed(2)}` : '';
    const subjectName = amountStr ? `${personName} — ${amountStr}` : personName;
    insertActivity({ subjectType: 'Contribution', subjectID: data.id, subjectName, verb: 'create' }).catch(() => {});

     return data;
};

export const deleteTransaction = async ({ids, mode = 'include', batchID}) => {
    const orgId = await getCurrentOrgID();
    const idsToDelete = typeof ids === "string" ? [ids] : [...ids];
    let theError;

    if(mode === 'exclude') {
        let query = supabase.from('ContributionTransactions').delete()
        if(idsToDelete && idsToDelete.length > 0) query.not('id', 'in', idsToDelete)
            
        const { error } = await query.eq('batchID', batchID).eq('organizationID', orgId)
        theError = error;
    } else if(mode === 'include') {
        const { error } = await supabase.from('ContributionTransactions').delete().in('id', idsToDelete).eq('batchID', batchID).eq('organizationID', orgId);
        theError = error;
    }

    if (theError) {
        ErrorHandler(Error(theError?.message), {query: "deleteTransaction", input: {ids, mode, error: theError}})
        throw Error(theError?.message);
    }

    return { success: true };
};

// Analytics Functions
export const getContributionsAnalytics = async ({ startDate, endDate, batchID, giverID, methodIds, amount }) => {
  const orgID = await getCurrentOrgID();

  const range = await reportRangeBounds(startDate, endDate);
  let query = supabase
    .from('ContributionTransactions')
    .select(`
      id,
      amount,
      transactionDate,
      createdAt,
      personID,
      methodID,
      methodDetailValue,
      batchID,
      Person:People(id, firstName, lastName, email),
      Method:ContributionMethods(id, name, detailName),
      Funds:ContributionFundTransactions(amount, Fund:ContributionFunds(id, name))
    `)
    .eq('organizationID', orgID)
    .in('status', ['PROCESSING', 'SUCCEEDED'])
    .order('transactionDate', { ascending: false });

  if (range.start) query = query.gte('transactionDate', range.start);
  if (range.end) query = query.lte('transactionDate', range.end);

  if (batchID) {
    query = query.eq('batchID', batchID);
  }

  if (giverID) {
    query = query.eq('personID', giverID);
  }

  if (methodIds?.length) {
    query = query.in('methodID', methodIds);
  }

  if (amount) {
    const min = amount.min !== '' && amount.min != null ? parseFloat(amount.min) : null;
    const max = amount.max !== '' && amount.max != null ? parseFloat(amount.max) : null;
    if (Number.isFinite(min)) {
      query = query.gte('amount', min);
    }
    if (Number.isFinite(max)) {
      query = query.lte('amount', max);
    }
  }

  const pageSize = 10000;
  let pageStart = 0;
  let allRows = [];

  for (;;) {
    const { data, error } = await query.range(pageStart, pageStart + pageSize - 1);

    if (error) {
      ErrorHandler(Error(error?.message), { query: "getContributionsAnalytics", input: { startDate, endDate, batchID, giverID, methodIds, amount, error } });
      throw Error(error?.message);
    }

    allRows = allRows.concat(data || []);

    if (!data || data.length < pageSize) {
      break;
    }

    pageStart += pageSize;
  }

  return allRows;
};

export const getContributionsKPIs = async ({ startDate, endDate }) => {
  const range = await reportRangeBounds(startDate, endDate);
  const { data, error } = await supabase.rpc('get_contributions_kpis', { p_start: range.start, p_end: range.end });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getContributionsKPIs", input: { startDate, endDate, error } });
    throw Error(error?.message);
  }

  return {
    totalTransactions: data?.totalTransactions ?? 0,
    totalAmount: Number(data?.totalAmount ?? 0),
    uniqueGivers: data?.uniqueGivers ?? 0
  };
};

export const getContributionsByMonth = async ({ startDate, endDate }) => {
  const range = await reportRangeBounds(startDate, endDate);
  const { data, error } = await supabase.rpc('get_contributions_by_month', { p_start: range.start, p_end: range.end });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getContributionsByMonth", input: { startDate, endDate, error } });
    throw Error(error?.message);
  }

  return (data || []).map(row => ({
    month: row.month,
    amount: Number(row.amount)
  }));
};

export const getTopGivers = async ({ startDate, endDate, limit = 20 }) => {
  const range = await reportRangeBounds(startDate, endDate);
  const { data, error } = await supabase.rpc('get_top_givers', { p_start: range.start, p_end: range.end, p_limit: limit });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getTopGivers", input: { startDate, endDate, limit, error } });
    throw Error(error?.message);
  }

  return (data || []).map(row => ({
    personID: row.personID,
    name: row.name,
    total: Number(row.total)
  }));
};

export const getGivingByMethod = async ({ startDate, endDate }) => {
  const range = await reportRangeBounds(startDate, endDate);
  const { data, error } = await supabase.rpc('get_giving_by_method', { p_start: range.start, p_end: range.end });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getGivingByMethod", input: { startDate, endDate, error } });
    throw Error(error?.message);
  }

  return (data || []).map(row => ({
    name: row.name,
    amount: Number(row.amount)
  }));
};

export const getGivingByFund = async ({ startDate, endDate }) => {
  const range = await reportRangeBounds(startDate, endDate);
  const { data, error } = await supabase.rpc('get_giving_by_fund', { p_start: range.start, p_end: range.end });

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getGivingByFund", input: { startDate, endDate, error } });
    throw Error(error?.message);
  }

  return (data || []).map(row => ({
    name: row.name,
    amount: Number(row.amount)
  }));
};

export const getContributionYears = async () => {
  const { data, error } = await supabase.rpc('get_contribution_years');

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getContributionYears", input: { error } });
    throw Error(error?.message);
  }

  return (data || [])
    .map(row => Number(row.year))
    .filter(Number.isFinite)
    .sort((a, b) => b - a);
};

export const getTransactionsByPerson = async ({ personID, personIDs, startDate, endDate, fundID, methodID, search }) => {
  const orgID = await getCurrentOrgID();

  let query = supabase
    .from('ContributionTransactions')
    .select(`
      id,
      amount,
      transactionDate,
      createdAt,
      personID,
      methodID,
      methodDetailValue,
      note,
      batchID,
      status,
      returnReason,
      Person:People(id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey),
      Method:ContributionMethods(id, name, detailName),
      Funds:ContributionFundTransactions(amount, Fund:ContributionFunds(id, name)),
      Payin:GivingPayins(id, payinID, status, returnReason, amount, methodType, metadata, rawPayload, createdAt)
    `)
    .eq('organizationID', orgID)
    .order('transactionDate', { ascending: false });

  if (personIDs?.length > 0) {
    query = query.in('personID', personIDs);
  } else {
    query = query.eq('personID', personID);
  }

  const range = await reportRangeBounds(startDate, endDate);
  if (range.start) query = query.gte('transactionDate', range.start);
  if (range.end) query = query.lte('transactionDate', range.end);
  if (fundID) query = query.eq('ContributionFundTransactions.fundID', fundID);
  if (methodID) query = query.eq('methodID', methodID);

  const { data, error } = await query;

  if (error) {
    ErrorHandler(Error(error?.message), { query: "getTransactionsByPerson", input: { personID, personIDs, error } });
    throw Error(error?.message);
  }

  let results = data || [];

  if (search) {
    const s = search.toLowerCase();
    results = results.filter(t =>
      t.Method?.name?.toLowerCase().includes(s) ||
      t.Funds?.some(f => f.Fund?.name?.toLowerCase().includes(s)) ||
      t.methodDetailValue?.toLowerCase().includes(s) ||
      t.note?.toLowerCase().includes(s) ||
      String(t.amount).includes(s)
    );
  }

  if (fundID) {
    results = results.filter(t => t.Funds?.some(f => f.Fund?.id === fundID));
  }

  return results;
};

export const importTransactions = async ({data, associations, mappedColumns, additionalParams}) => {
    const orgID = await getCurrentOrgID();
    const org = await getOrganization(orgID);
    const orgTimezone = org?.timezone || 'America/Chicago';
    await upsertImportAssociations(associations.map((association) => ({...association, organizationID: orgID})));

    const associationTypeByColumn = importFields.transactions.reduce((acc, field) => {
        if(field.type) acc[field.name] = field.type;
        return acc;
    }, {});
    const normalizeAssociationTable = (table) => String(table ?? '').trim().toLowerCase();
    const normalizeAssociationValue = (value) => String(value ?? '').trim().toLowerCase();
    const findAssociationForColumn = (column, value) => {
        const associatedTable = associationTypeByColumn[column];
        if(!associatedTable || !associations?.length) return null;

        const tableKey = normalizeAssociationTable(associatedTable);
        const valueKey = normalizeAssociationValue(value);
        const matches = associations.filter((association) => normalizeAssociationTable(association.associatedTable) === tableKey && normalizeAssociationValue(association.value) === valueKey);
        return matches.find((association) => association.suggested !== true) || matches[0] || null;
    };

    //Import the transactions
    const transactionsToImport = data.map((row) => {
        const transaction = {
            batchID: additionalParams.batchID,
            organizationID: orgID,
        }

        for(const [column, csvHeader] of Object.entries(mappedColumns)){
            const association = findAssociationForColumn(column, row[csvHeader]);

            if(association){
                transaction[column] = association.associatedID;
            }
            else{
                transaction[column] = row[csvHeader];
            }
        }

        // Adjust date-only values in datetime columns so they represent midnight
        // in the org's timezone rather than midnight UTC. Without this, a date
        // like 02/15/2026 stored as 2026-02-15T00:00:00Z would display as Feb 14
        // in a timezone behind UTC (e.g. Central time).
        for(const col of TRANSACTION_DATETIME_COLUMNS){
            if(transaction[col] != null){
                transaction[col] = adjustDateOnlyForTimezone(transaction[col], orgTimezone);
            }
        }

        return transaction;
    })

    const chunkSize = 2500;
    const chunks = [];

    for(let i = 0; i < transactionsToImport.length; i += chunkSize){
        chunks.push(transactionsToImport.slice(i, i + chunkSize));
    }

    for(const chunk of chunks){
        const fundsToImport = [];

        for(const transaction of chunk){
            transaction.id = Crypto.randomUUID();
            if(transaction.fundID){
                fundsToImport.push({transactionID: transaction.id, fundID: transaction.fundID, organizationID: orgID, amount: transaction.amount});
                delete transaction.fundID;
            }
        }

        const { error: transactionError } = await supabase.from('ContributionTransactions').insert(chunk);

        if (transactionError) {
            ErrorHandler(Error(transactionError?.message), {query: "importTransactions", input: {data, associations, mappedColumns, error: transactionError}})
            throw Error(transactionError?.message);
        }

        if(fundsToImport.length > 0){
            const { error: fundError } = await supabase.from('ContributionFundTransactions').insert(fundsToImport);

            if (fundError) {
                ErrorHandler(Error(fundError?.message), {query: "createTransaction", input: {fundsToImport, error: fundError}})
                throw Error(fundError?.message);
            }
        }
    }

    queryClient.invalidateQueries({queryKey: ['importAssociations']});
}
